import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, IconAlertOctagon, IconRefresh } from '@/design-system';
import { ApiError } from '@/lib/api';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** Nome da área, exibido na mensagem. Ex.: "Lista Mestra". */
  readonly area?: string;
  readonly onReset?: () => void;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly correlationId: string | null;
}

/**
 * Fronteira de erro.
 *
 * REGRA DE REDAÇÃO em ambiente regulado: a mensagem precisa deixar claro que
 * NADA foi alterado. Depois de uma tela quebrar, a primeira dúvida do usuário é
 * se a operação foi gravada pela metade — e "algo deu errado" sozinho não
 * responde isso. Também expomos o código de correlação, que liga o incidente ao
 * log técnico e à trilha de auditoria.
 *
 * ACESSIBILIDADE: o bloco é `role="alert"`, então o leitor de tela anuncia a
 * falha assim que ela substitui o conteúdo; e o botão de recuperação recebe
 * foco programático, porque o elemento que tinha o foco deixou de existir.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, correlationId: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      correlationId: error instanceof ApiError ? error.correlationId : null,
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sem serviço de telemetria configurado: o console é o canal de diagnóstico
    // local. Nunca registramos dado de negócio aqui — o log do browser não é
    // ambiente controlado.
    console.error('[ErrorBoundary]', error.message, info.componentStack);
  }

  private readonly reset = () => {
    this.setState({ error: null, correlationId: null });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    const { error, correlationId } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="mx-auto max-w-2xl p-6">
        <Card>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-subtle text-danger-content"
            >
              <IconAlertOctagon size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-content">
                {this.props.area
                  ? `Algo deu errado em ${this.props.area}`
                  : 'Algo deu errado nesta tela'}
              </h1>
              <p className="mt-2 text-sm text-content-muted">
                <strong className="font-semibold text-content">
                  Nenhuma alteração foi registrada.
                </strong>{' '}
                A tela não conseguiu ser exibida, mas nada foi gravado no sistema. Você pode tentar
                carregar novamente.
              </p>

              <p className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-content-muted">
                <span className="font-medium">Detalhe técnico: </span>
                <span className="font-mono">{error.message}</span>
                {correlationId ? (
                  <>
                    <br />
                    <span className="font-medium">Código de correlação: </span>
                    <span className="font-mono">{correlationId}</span>
                  </>
                ) : null}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  leadingIcon={<IconRefresh size={15} />}
                  onClick={this.reset}
                  // Foco automático: o elemento antes focado sumiu com a árvore.
                  autoFocus
                >
                  Tentar novamente
                </Button>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Recarregar a página
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}
